import { DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SeConstraintNotifierService } from '@se/constraint';
import { IConfigControlMeasure, IDataSourceItem } from '@se/cp.common';
import { CultureService } from '@se/platform';
import { DataBinding, Logger } from '@se/se.ui.html5';
import { filter, Observable, take, takeUntil } from 'rxjs';
import { CustomPageControlGridModalComponent } from '../../cp-ui-engine/custom-page-control-grid-modal/custom-page-control-grid-modal.component';
import { ICpEntity, PropertyType } from '../../cp-configuration/view-model';
import { CpMeasureControl, NumericValues } from '../../cp-configuration/view-model/cp-measure-control';
import { CpModelService } from '../../cp-model.service';
import { CpDialogService } from '../../cp-dialog-service';
import * as fromCp from '../../store/cp.state';
import { selectCpDialogVisible } from '../../store/cp.selectors';
import { CpControlGrid } from '../../cp-configuration/utils/control-grid';
import { AbstractControlComponent } from '../abstract-control';
import { CpControlDataService } from '../control-data.service';
import { isNil } from 'lodash-es';
import { CpReportService } from '../../cp-report.service';

type ModelType = string | number | null | undefined;

@Component({
    selector: 'cp-measure',
    templateUrl: './measure.component.html',
    styleUrls: ['./measure.component.scss'],
})
export class CpMeasureComponent extends AbstractControlComponent implements AfterViewInit, OnDestroy {
    additionalLabelInfo: string | undefined = '';
    badgeSource$!: Observable<string | null>;
    control!: CpMeasureControl;
    config!: IConfigControlMeasure;
    formControl!: FormControl;
    hideUnitOfMeasure: boolean = true;
    icon?: string;
    method!: IDataSourceItem;
    model: NumericValues = { numModel: 0, strModel: '', separator: ',' };
    observer: any;
    placeholderUnitOfMeasureDisplayed: string = '';
    prevWidth: number = 0;
    shownInfo!: string;
    tooltipInputMeasure!: string;
    unitOfMeasureDisplayed!: string;

    private decimalPipeDigitConfiguration?: string;
    private separatorInUse = this.cultureService.getDecimalSeparator();
    readonly pattern = '^([+\\-])?(\\d+)?([\\.,])?(\\d+)?$';
    private decimalDigits!: number;
    private minNormalValue$!: Observable<number | undefined>;
    private maxNormalValue$!: Observable<number | undefined>;
    private normalityReferenceValue$!: Observable<number | undefined>;
    private formulaReference?: string;
    private formulaDescription?: string;
    private conversionFactor!: number;
    private minNormalValue!: number | undefined;
    private maxNormalValue!: number | undefined;
    private normalityReferenceValue!: number | undefined;
    private measureTooltip!: string | null | undefined;

    @ViewChild('inputElement', { read: ElementRef }) inputElement?: ElementRef<HTMLElement>;
    @ViewChild('invisibleElement', { read: ElementRef }) invisibleElement?: ElementRef<HTMLElement>;
    @ViewChild('unitOfMeasure', { read: ElementRef }) unitOfMeasure?: ElementRef<HTMLElement>;
    @ViewChild('field', { read: ElementRef }) field?: ElementRef<HTMLElement>;
    @ViewChild('measureIcon', { read: ElementRef }) measureIcon?: ElementRef<HTMLElement>;

    constructor(
        private cultureService: CultureService,
        private modelService: CpModelService,
        private translate: TranslateService,
        private decimalPipe: DecimalPipe,
        private dialogService: CpDialogService,
        private controlData: CpControlDataService,
        private store: Store<fromCp.RootState>,
        private reportService: CpReportService,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger,
        private elRef: ElementRef,
    ) {
        super(cdr, constraintNotifierService, logger);

        this.observer = new ResizeObserver(() => {
            this.hideUnitOfMeasure = this.isOverflow();
        });

        this.observer.observe(this.elRef.nativeElement);
    }

    protected init(): void {
        this.control.setModelService(this.modelService);
        this.control.getEntityObservable().subscribe((value) => this._entitySubject.next(value));

        this.config = this.control.config;
        this.icon = this.control.getIcon();
        this.measureTooltip = this.config.tooltip;
        this.retrieveDynamicProperties();

        if (!isNil(this.decimalDigits) && this.decimalDigits.toString() !== '') {
            this.decimalPipeDigitConfiguration = '1.' + this.decimalDigits + '-' + this.decimalDigits;
        } else {
            this.decimalPipeDigitConfiguration = '1.0-0';
        }

        this.createForm();
        this.checkForModelUpdate();
        this.checkNormalityReferenceValue();
        this.checkOutOfRangeMinValue();
        this.checkOutOfRangeMaxValue();
        this.checkOutOfRange();
        this.registerBadge();
        this.eventuallyDestroyControl();

        this.findDetailsSelectedMethod();
    }

    ngOnDestroy() {
        this.observer.unobserve(this.elRef.nativeElement);
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();

        this.hideUnitOfMeasure = this.isOverflow();
        this.cdr.detectChanges();
    }

    getMeasureEntity() {
        return this._entitySubject.value;
    }

    private refreshLabelInfo() {
        let methodInfo = this.method ? this.method.getPropertyPrimitiveValue('Description') : '';
        if (this.shownInfo) {
            this.additionalLabelInfo = ' ' + this.shownInfo;
        }
        this.additionalLabelInfo = ' ' + methodInfo;
    }

    private retrieveDynamicProperties() {
        this.conversionFactor = this.control.retrieveConversionFactor();
        this.decimalDigits = this.control.retrieveDecimalDigits();
        this.formulaDescription = this.control.retrieveFormulaDescription();
        this.formulaReference = this.control.retrieveFormulaReference();
        this.minNormalValue$ = this.control.retrieveMinNormalValue(this._entitySubject);
        this.maxNormalValue$ = this.control.retrieveMaxNormalValue(this._entitySubject);
        this.normalityReferenceValue$ = this.control.retrieveNormalityReferenceValue(this._entitySubject);
        this.unitOfMeasureDisplayed = this.control.retrieveUnitOfMeasureDisplayed();
        this.shownInfo = !!this.control.retrieveShownInfo() ? this.control.retrieveShownInfo() : '';
    }

    private getDetails() {
        const detailBinding = this.control.dataBinding.entity.addSimple('Detail');
        const details: ICpEntity[] =
            this.modelService.getEntity(detailBinding.entity, true)?.getAllEntities(new DataBinding('Detail')) ?? [];
        return details;
    }

    private findDetailsSelectedMethod() {
        const details = this.getDetails();
        if (details && details.length > 0) {
            const detailsUsedForcalc = details.filter((detail) => {
                return detail.getPropertyValue('UsedForCalc') === true;
            });
            const detailsUsedForcalcFiltered = detailsUsedForcalc.filter((detailUsedForCalc) => {
                return detailUsedForCalc.getPropertyValue('IdUsMeasureMethod') !== null;
            });
            this.method =
                !!detailsUsedForcalcFiltered && detailsUsedForcalcFiltered.length > 0
                    ? (detailsUsedForcalcFiltered[0].getPropertyValue('IdUsMeasureMethod') as any)
                    : '';

            this.hasAIData = detailsUsedForcalc.some((detail) => {
                return (
                    detail.getPropertyValue('IdMeasureSource') ===
                    this.reportService.measureSourceUS2AI?.getPropertyPrimitiveValue('Id')
                );
            });
        }
        this.refreshLabelInfo();
    }

    private createForm() {
        const validators = this.computeValidators();
        const initialValue: number = this.control.getInitialValue() as number;
        const initialStrModel = initialValue
            ? this.makeStringModel(this.convertNumModelToViewValue(initialValue), this.separatorInUse)
            : null;
        this.model = initialValue ? this.getValues(initialStrModel ?? null) : this.getEmptyModel();
        this.formControl = new FormControl(this.model.strModel, validators);
        this.disableControlWhenFormulaPresent();
    }

    private computeValidators(): ValidatorFn[] {
        let validators = [];
        validators.push(
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required),
        );
        return validators;
    }

    private makeStringModel(numModel: number, separator: string) {
        return (
            this.decimalPipe
                .transform(numModel, this.decimalPipeDigitConfiguration, 'en')
                ?.split(',')
                .join('')
                ?.replace('.', separator) ?? null
        );
    }

    private convertNumModelToViewValue(numModel: number) {
        return numModel * this.conversionFactor;
    }

    private disableControlWhenFormulaPresent() {
        if (!!this.formulaDescription) {
            this.control.makeControlReadOnly();
            this.formControl.disable({
                onlySelf: true,
                emitEvent: true,
            });
        }
    }

    private getValues(input: string | null): NumericValues {
        if (!input) {
            return { numModel: null, separator: null, strModel: null };
        }

        const matches = input.trim().match(new RegExp(this.pattern));
        if (!matches) {
            return { numModel: null, separator: null, strModel: input };
        }

        const numModel = parseFloat(matches[0].replace(',', '.'));
        const separator = matches[3] ?? this.separatorInUse;
        const strModel = this.makeStringModel(numModel, separator);
        return { numModel: this.convertViewValueToNumModel(numModel), separator: separator, strModel: strModel };
    }

    private getEmptyModel(): NumericValues {
        return { numModel: null, strModel: '', separator: this.separatorInUse };
    }

    private checkForModelUpdate() {
        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                // Ignora update
                if (!this.isAcceptable(model)) {
                    this.logger?.error(`Invalid model update for ${this.control.name}: ${model}`);
                    return;
                }
                this.logger?.trace(`Model update ${this.control.name} ${model}`);
                this.updateFormControlValue(model);
                this.checkOutOfRange();
            });
    }

    private isAcceptable(model: PropertyType | undefined): model is ModelType {
        return model === null || model === undefined || typeof model === 'number' || typeof model === 'string';
    }

    private updateFormControlValue(model: ModelType) {
        if (!this.formControl) {
            return;
        }

        let strModel: string | null;
        let numModel: number | null;

        if (model === undefined || model === null) {
            // Per type-guard
            strModel = null;
            numModel = null;
        } else if (typeof model === 'string') {
            let parsed = this.getValues(model);
            strModel = parsed.strModel;
            numModel = parsed.numModel;
            this.separatorInUse = parsed.separator ?? this.separatorInUse;
        } else {
            numModel = model;
            strModel = this.makeStringModel(this.convertNumModelToViewValue(numModel), this.separatorInUse);
        }

        if (this.model && numModel !== this.model.numModel) {
            this.model.numModel = numModel;
            this.formControl.setValue(strModel, { emitViewToModelChange: false });
        }

        this.detectChanges(this.model);
    }

    private checkNormalityReferenceValue() {
        this.normalityReferenceValue$.subscribe((normValue) => {
            this.normalityReferenceValue = normValue;
            this.checkOutOfRange();
        });
    }

    private checkOutOfRangeMinValue() {
        this.minNormalValue$.subscribe((min) => {
            this.minNormalValue = min;
            this.checkOutOfRange();
        });
    }

    private checkOutOfRangeMaxValue() {
        this.maxNormalValue$.subscribe((max) => {
            this.maxNormalValue = max;
            this.checkOutOfRange();
        });
    }

    private checkOutOfRange() {
        const valueToCheck = this.normalityReferenceValue ?? this.model.numModel ?? null;
        if (valueToCheck === null) {
            this.warningMessage = null;
            return;
        }

        let isOutOfRange = false;
        const delta = 10 ** -this.decimalDigits;

        if (this.minNormalValue !== undefined && !isNaN(this.minNormalValue)) {
            isOutOfRange ||= valueToCheck - delta < this.minNormalValue;
        }

        if (this.maxNormalValue !== undefined && !isNaN(this.maxNormalValue)) {
            isOutOfRange ||= valueToCheck + delta > this.maxNormalValue;
        }

        /*accanto alla barra dei filtri aggingere icona che dia warining di misure out of range basandosi sull'emitter qui sotto
         * dato che il bubble delle'vento viene fatto su component.instance bisogna in qualche modo raccogliere il solo evento delle misure
         * o si filtra per stringa nel messaggio 'MeasureOutOfRange' oppure si controlla sul component.instance il tipo di component
         **/
        this.warningMessage = isOutOfRange ? 'MeasureOutOfRange' : null;
        this.findDetailsSelectedMethod();
    }

    private registerBadge() {
        this.badgeSource$ = this.controlData
            .getBadge$(this.control.dataBinding.entity)
            .pipe(takeUntil(this.destroyed$));
    }

    private eventuallyDestroyControl() {
        this.destroyed$.subscribe(() => this.control.destroyCpComponent());
    }

    //protected handleTooltipNotification(tooltip: string) {
    //    this.config.additionalInformation = tooltip;
    //}

    private detectChanges(model: NumericValues) {
        if (model !== null && model.numModel !== null && isNaN(model.numModel)) {
            return false;
        }

        const changed = this.control.onModelChange(this.modelService, model.numModel, !this.isDisabled);

        return changed;
    }

    public onMeasureChange() {
        this.model = this.getValues(this.formControl?.value);

        if (isNil(this.model.numModel) && isNil(this.model.strModel)) {
            this.checkLastManualDetail();
            if (this.getDetails().length === 0) {
                return;
            }
        }

        this.formControl?.setValue(this.model.strModel, { emitEvent: false });

        this.detectChanges(this.model);
        this.setUpIdMeasureCalcultionToLast();

        this.checkOutOfRange();

        this.updateDetails();

        this.edited.emit();
    }

    private checkLastManualDetail() {
        const details = this.getDetails();

        let lastIndex: number | null = null;
        for (let i: number = details.length - 1; i >= 0; i--) {
            if (details[i].getPropertyValue('UsedForCalc') === true) {
                lastIndex = i;
                break;
            }
        }

        let detailBinding: DataBinding = this.control.dataBinding.entity.addSimple('Detail');

        const measureCalculationBinding: ICpEntity = this.modelService.getValue(
            this.control.dataBinding.entity.addSimple('IdUsMeasureCalculation'),
        ) as ICpEntity;

        if (
            (!!lastIndex || lastIndex === 0) &&
            details[lastIndex].getPropertyValue('IsManualModified') &&
            measureCalculationBinding.getPropertyValue('Id') === '4f94abf5-96e7-4f87-bd01-3330e246226c'
        ) {
            this.modelService.removeEntity(details[lastIndex]);
            const updateddetails = this.getDetails();
            if (updateddetails.length === 0) {
                this.modelService.setValue(detailBinding, []);
            } else {
                this.removeFlagUsedForCalcToAllDetails(updateddetails);
                updateddetails.forEach((entity, index) => {
                    if (isNil(entity.getPropertyValue('Value'))) {
                        updateddetails.splice(index, 1);
                    }
                });

                this.modelService.setValue(detailBinding, updateddetails);
            }
        } else {
            this.removeFlagUsedForCalcToAllDetails(details);
        }
    }

    private removeFlagUsedForCalcToAllDetails(details: ICpEntity[]) {
        for (let i: number = details.length - 1; i >= 0; i--) {
            const usedForCalcBinding: DataBinding = details[i].path?.addSimple('UsedForCalc') ?? new DataBinding();

            details[i].setPropertyValue('UsedForCalc', false);
            this.modelService.setValue(usedForCalcBinding, false);
        }
    }

    private setUpIdMeasureCalcultionToLast() {
        this.modelService.setDatasourceValue(
            this.control.dataBinding.entity.addSimple('IdUsMeasureCalculation'),
            '4f94abf5-96e7-4f87-bd01-3330e246226c',
            'lsUsMeasureCalculation',
        );
    }

    updateDetails() {
        const detailBinding = this.control.dataBinding.entity.addSimple('Detail');
        const details = this.getDetails();

        if (!!details && details.length === 0) {
            this.addNewDetail(detailBinding);
            return;
        }

        if (!!this.model.numModel) {
            this.checkLastUsedForcalcAndManualModified(details, this.model.numModel);
        }
    }

    private checkLastUsedForcalcAndManualModified(details: ICpEntity[], numModel: number) {
        let lastIndex: number | null = null;

        for (let i: number = details.length - 1; i >= 0; i--) {
            if (details[i].getPropertyValue('UsedForCalc') === true) {
                lastIndex = i;
                break;
            }
        }

        let detailBinding: DataBinding = this.control.dataBinding.entity.addSimple('Detail');

        if ((!!lastIndex || lastIndex === 0) && details[lastIndex].getPropertyValue('IsManualModified')) {
            let databindingDetailToModify: DataBinding = detailBinding.addIndex(lastIndex).addSimple('Value');
            this.modelService.setValue(databindingDetailToModify, numModel);
        } else {
            this.addNewDetail(detailBinding);
        }
    }

    addNewDetail(detailBinding: DataBinding) {
        let details: ICpEntity | undefined = this.modelService.getEntity(detailBinding, true);
        if (!details) {
            throw new Error('Could not find or create measure details entity list.');
        }

        let newEntity: ICpEntity | null = this.modelService.makeEntityFromDataset(detailBinding, details);

        if (!newEntity) {
            throw new Error('Could not create measure detail entity.');
        }

        details?.addEntityToList(new DataBinding(), newEntity);
        newEntity?.setPropertyValue('Value', this.model.numModel);
        newEntity?.setPropertyValue('UsedForCalc', true);
        newEntity?.setPropertyValue('IsManualModified', true);

        this.modelService.setValue(detailBinding, details);
    }

    private convertViewValueToNumModel(numModel: number) {
        return numModel ? numModel / this.conversionFactor : null;
    }

    private tooltipApplyNormalValues() {
        if (this.normalityReferenceValue === null || this.normalityReferenceValue === undefined) {
            const minValueNormalized = this.minNormalValue ? this.convertNumModelToViewValue(this.minNormalValue) : 0;
            const maxValueNormalized = this.maxNormalValue ? this.convertNumModelToViewValue(this.maxNormalValue) : 0;
            this.tooltipInputMeasure =
                ': ' + minValueNormalized + ' - ' + maxValueNormalized + ' ' + this.unitOfMeasureDisplayed;
            return this.tooltipInputMeasure;
        }

        if (this.minNormalValue === undefined && !!this.maxNormalValue) {
            this.tooltipInputMeasure = ': < ' + this.maxNormalValue;
            return this.tooltipInputMeasure;
        }

        if (!!this.minNormalValue && this.maxNormalValue === undefined) {
            this.tooltipInputMeasure = ': > ' + this.minNormalValue;
            return this.tooltipInputMeasure;
        }
        this.tooltipInputMeasure = ': ' + this.minNormalValue + ' - ' + this.maxNormalValue;
        return this.tooltipInputMeasure;
    }

    public onFocusInputMeasure(value?: string) {
        let firstTooltipField: string = this.measureTooltip ? this.measureTooltip : this.control.name;

        if (this.hideUnitOfMeasure) {
            this.tooltipInputMeasure = firstTooltipField + ' [' + this.unitOfMeasureDisplayed + ']';
        } else {
            this.tooltipInputMeasure = firstTooltipField;
        }

        if (!!this.config?.additionalInformation && !!this.normalityReferenceValue) {
            this.tooltipInputMeasure += '\n' + this.config?.additionalInformation + ' ' + this.normalityReferenceValue;
        }

        if (this.minNormalValue === undefined && this.maxNormalValue === undefined) {
            this.tooltipInputMeasure += '';
        } else {
            this.tooltipInputMeasure +=
                ' \n' + this.translate.instant('ApplyNormalValues') + this.tooltipApplyNormalValues();
        }

        if (!!this.formulaDescription) {
            this.tooltipInputMeasure += this.makeFormulaDescriptionTooltip();
        }

        if (!!this.formulaReference) {
            this.tooltipInputMeasure += this.makeFormulaReferenceTooltip();
        }

        if (value) {
            this.tooltipInputMeasure += ' - \n' + this.translate.instant('CurrentValue') + ': ' + value;
        }

        return this.tooltipInputMeasure;
    }

    private makeFormulaDescriptionTooltip(): string {
        return '\n' + this.translate.instant('FormulaDescription') + ': ' + this.formulaDescription;
    }

    private makeFormulaReferenceTooltip(): string {
        return '\n' + this.translate.instant('FormulaReference') + ': ' + this.formulaReference;
    }

    onFocus() {
        this.focusin.emit();
    }

    protected onModelChange(_: string | null) {
        this.model = this.getValues(this.formControl?.value);
        return this.detectChanges(this.model);
    }

    onPanelButtonClick() {
        const grid = CpControlGrid.fromPanel(this.control.createDetailsVirtualPanel(), this.control.dataBinding.entity);
        if (!grid) {
            throw new Error('Invalid panel configuration.');
        }

        const panelConfig: Partial<fromCp.CpDialogState> = {};
        panelConfig.fixedWidthPx = 968;
        panelConfig.noPaddingInContent = true;
        panelConfig.zIndex = 9999;
        panelConfig.closable = false;
        this.findDetailsSelectedMethod();

        this.dialogService.open(CustomPageControlGridModalComponent, { grid, control: this.control }, panelConfig);

        this.store
            .pipe(
                select(selectCpDialogVisible),
                filter((isVisible) => !isVisible),
                takeUntil(this.destroyed$),
                take(1),
            )
            .subscribe(() => this.onCpDialogVisibleChange());
    }

    onCpDialogVisibleChange() {
        if (!this.dialogService.param) {
            this.onBlur();
        }
    }

    onFocusIn() {
        this.hideUnitOfMeasure = true;
        this.placeholderUnitOfMeasureDisplayed = '';
    }

    onBlur() {
        this.placeholderUnitOfMeasureDisplayed = this.unitOfMeasureDisplayed;
        this.hideUnitOfMeasure = this.isOverflow();
    }

    _checkIfMeasureIsNotPresent() {
        return this.model.numModel === undefined || this.model.numModel === null || this.model.numModel === 0;
    }

    isOverflow(): boolean {
        if (this._checkIfMeasureIsNotPresent()) {
            return false;
        }
        if (
            this.inputElement &&
            this.field?.nativeElement.offsetWidth !== undefined &&
            this.invisibleElement?.nativeElement.offsetWidth !== undefined &&
            this.measureIcon?.nativeElement.offsetWidth !== undefined &&
            this.unitOfMeasure?.nativeElement.offsetWidth !== undefined
        ) {
            let availableWidth = this.field.nativeElement.offsetWidth - 10;
            let usedWidth =
                this.invisibleElement.nativeElement.offsetWidth +
                this.measureIcon.nativeElement.offsetWidth +
                this.unitOfMeasure.nativeElement.offsetWidth +
                10;
            return usedWidth >= availableWidth;
        }
        return false;
    }
}
